// First-run coachmark (R-031) — one small, dismissible pointer at the queue
// scope pill, shown once per user, and then never again.
//
// ── WHY THIS IS NOT A TOUR ────────────────────────────────────────────────
// The Blueprint asks for a coachmark, not an onboarding sequence. A nurse opens
// this app between patients; anything that has to be clicked through before she
// can work is a tax on every one of those visits. So the rules here are strict
// and they are enforced by construction, not by discipline:
//
//   NEVER BLOCKING — there is no overlay and no focus steal. The bubble sits
//     next to the pill; every control underneath stays clickable and stays in
//     the tab order. The P2 house rule (a modal must hit-test on top) is the
//     mirror image of this one: this thing must hit-test UNDER everything, so
//     it is pointer-events:none except for its own dismiss button.
//   ONCE — the seen flag is written the moment it is SHOWN, not when it is
//     dismissed. If a nurse ignores it and closes the tab, it has had its turn.
//     A hint that reappears until you interact with it is a modal wearing a
//     costume.
//   KEYBOARD — Escape dismisses it, and the dismiss button is reachable by Tab.
//   STORAGE-SAFE — the same R-030 problem applies: on a device that cannot
//     persist, `seen` never sticks. We resolve that in favour of NOT nagging:
//     when storage is unavailable the coachmark is suppressed entirely rather
//     than shown on every single load. A hint nobody can dismiss permanently is
//     worse than a hint nobody sees.

import { esc } from './esc.js';
import { storageAvailable } from '../mode.js';

/** Per-user key prefix. Per-user because clinic laptops are shared. */
export const COACH_KEY_PREFIX = 'tumaini_coach_';

/** The scope coachmark's id (the only one so far — the module is general). */
export const COACH_SCOPE = 'scope';

/** The R-031 copy, verbatim from the Blueprint's chrome row. */
export const COACH_SCOPE_TEXT =
  'This is your queue. Switch to see a colleague’s or the whole team’s.';

/**
 * @param {string} id coachmark id
 * @param {string|null|undefined} uid auth user id
 * @returns {string}
 */
export function coachKey(id, uid) {
  return COACH_KEY_PREFIX + id + '_' + (uid || 'anon');
}

/**
 * PURE decision: should this coachmark be shown? Split out from the DOM so the
 * once-only rule and the storage rule are testable without a browser.
 *
 * @param {{seen: any, storageOk: boolean, anchorPresent: boolean}} ctx
 *   seen — the raw stored flag (any truthy string means seen);
 *   storageOk — can this browser persist at all (see the storage rule above);
 *   anchorPresent — is the element the bubble points at actually on screen? A
 *     coachmark pointing at nothing is worse than none: it describes a control
 *     the user cannot find.
 * @returns {boolean}
 */
export function shouldShowCoach(ctx) {
  if (!ctx || !ctx.anchorPresent) return false;
  if (!ctx.storageOk) return false;
  return !ctx.seen;
}

/**
 * Read the seen flag. Never throws.
 * @param {string} id
 * @param {string|null|undefined} uid
 * @param {any} [storage]
 * @returns {string|null}
 */
export function readCoachSeen(id, uid, storage) {
  try {
    const st = storage || /** @type {any} */ (globalThis).localStorage;
    return st.getItem(coachKey(id, uid));
  } catch {
    return null;
  }
}

/**
 * Mark the coachmark seen. Never throws; returns whether it stuck.
 * @param {string} id
 * @param {string|null|undefined} uid
 * @param {any} [storage]
 * @returns {boolean}
 */
export function markCoachSeen(id, uid, storage) {
  try {
    const st = storage || /** @type {any} */ (globalThis).localStorage;
    const key = coachKey(id, uid);
    st.setItem(key, '1');
    return st.getItem(key) === '1';
  } catch {
    return false;
  }
}

/**
 * Remove any coachmark currently on screen. If focus was INSIDE the bubble
 * (the "Got it" button, or Escape pressed while on it) it is returned to the
 * anchor the bubble pointed at — removing the focused element would otherwise
 * drop a keyboard user to <body>, i.e. the top of the document.
 * @returns {void}
 */
export function dismissCoach() {
  const d = /** @type {any} */ (globalThis).document;
  if (!d) return;
  const el = d.getElementById('coachmark');
  if (el && el.parentNode) {
    const hadFocus = !!(d.activeElement && typeof el.contains === 'function' && el.contains(d.activeElement));
    const anchorId = typeof el.getAttribute === 'function' ? el.getAttribute('data-anchor') : null;
    el.parentNode.removeChild(el);
    const anchor = hadFocus && anchorId ? d.getElementById(anchorId) : null;
    if (anchor && typeof anchor.focus === 'function') {
      try {
        anchor.focus();
      } catch {
        /* focus best-effort */
      }
    }
  }
  if (_escHandler) {
    d.removeEventListener('keydown', _escHandler);
    _escHandler = null;
  }
}

/** @type {((e: any) => void)|null} */
let _escHandler = null;

/**
 * Show a coachmark anchored under `anchorId`, if it has not been shown before.
 * Returns whether it was rendered (so callers/tests can assert the once-rule).
 *
 * The bubble is appended to <body> and positioned from the anchor's rect, not
 * nested inside the anchor's own container: the scope pill lives in a strip
 * with `overflow` rules that would clip an absolutely-positioned child, which
 * is how these things end up half-visible on a narrow laptop.
 *
 * @param {{id?: string, anchorId: string, text: string, uid?: string|null, storage?: any}} opts
 * @returns {boolean}
 */
export function showCoach(opts) {
  const d = /** @type {any} */ (globalThis).document;
  if (!d || !d.body) return false;
  const id = opts.id || COACH_SCOPE;
  const anchor = d.getElementById(opts.anchorId);
  const storage = opts.storage;
  const decision = shouldShowCoach({
    seen: readCoachSeen(id, opts.uid, storage),
    storageOk: storageAvailable(storage),
    anchorPresent: !!anchor,
  });
  if (!decision) return false;

  // Written BEFORE the bubble exists: see the ONCE rule. If anything below
  // throws, the user still does not get it again on the next load.
  markCoachSeen(id, opts.uid, storage);

  dismissCoach(); // never stack two

  const box = d.createElement('div');
  box.id = 'coachmark';
  box.className = 'coachmark';
  box.setAttribute('data-anchor', opts.anchorId); // read back by dismissCoach()
  // role="status" (not alert/dialog): announced politely, does not demand
  // acknowledgement, does not move focus. That is exactly the promise the
  // NEVER-BLOCKING rule makes to a screen-reader user too.
  box.setAttribute('role', 'status');

  // Insert the region EMPTY, then fill it. A live region only announces content
  // that changes after it is in the accessibility tree — appending a
  // role="status" node that already holds its text is the canonical way to get
  // silence from NVDA, JAWS and VoiceOver alike. Because markCoachSeen() has
  // already been written, a screen-reader user's one and only chance to hear
  // this hint was being consumed without a word being spoken.
  d.body.appendChild(box);
  position(box, anchor);
  const html =
    `<span class="coach-txt">${esc(opts.text)}</span>` +
    '<button type="button" class="coach-x" data-action="coach-dismiss" aria-label="Dismiss this hint">Got it</button>';
  const fill = () => {
    // The bubble may already have been dismissed inside the tick.
    if (box.isConnected === false) return;
    box.innerHTML = html;
    position(box, anchor); // width changes once there is text in it
  };
  const w = /** @type {any} */ (globalThis);
  if (typeof w.requestAnimationFrame === 'function') w.requestAnimationFrame(fill);
  else setTimeout(fill, 0);

  _escHandler = (/** @type {any} */ e) => {
    if (e.key === 'Escape') dismissCoach();
  };
  d.addEventListener('keydown', _escHandler);
  return true;
}

/**
 * Place the bubble under the anchor, kept inside the viewport. Guarded for the
 * headless/jsdom case where getBoundingClientRect returns zeros — then the CSS
 * default position applies rather than a bubble pinned to 0,0.
 * @param {any} box
 * @param {any} anchor
 * @returns {void}
 */
function position(box, anchor) {
  const w = /** @type {any} */ (globalThis);
  if (!anchor || typeof anchor.getBoundingClientRect !== 'function') return;
  const r = anchor.getBoundingClientRect();
  if (!r || (!r.width && !r.height)) return;
  const scrollY = w.scrollY || 0;
  const scrollX = w.scrollX || 0;
  const vw = (w.innerWidth || 0) || 1024;
  const top = r.bottom + scrollY + 10;
  // Clamp so a pill near the right edge does not push the bubble off-screen.
  const maxLeft = Math.max(8, vw - 340);
  const left = Math.min(Math.max(8, r.left + scrollX), maxLeft);
  box.style.top = top + 'px';
  box.style.left = left + 'px';
}
